import React from "react"
import PropTypes from "prop-types"
import { Flex, Tooltip } from "antd"
import { Icon } from "ui/Icon"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { getOriginalIfDiscounted, getTicketRateComparison } from "../pricing"
import Counter from "./Counter"
import PanelSection from "./PanelSection"
import styles from "./BookingPanel.module.css"

// "Add Guest Tickets": shares the Total Guests places out between the ticket types. Every "+" stops
// once all places have a ticket ("Maximum of N places reached"), and a type's "+" also stops at
// that type's own limit: its places left at the chosen start time (`typeLimits`), else its
// max_capacity. Each type shows its price for the chosen date, with the base price struck through
// when the date lowers it. `warning` is the "Availability has changed" message, shown over the
// tickets.
const TicketsSection = ({ experience, ticketTypes, date, totalGuests, ticketCounts, typeLimits, warning, onChange, error }) => {
  const { t } = useTranslation()
  const { rates, baseRates } = getTicketRateComparison({ experience, date })
  const ticketsChosen = ticketTypes.reduce((sum, ticketType) => sum + Number(ticketCounts?.[ticketType.id] || 0), 0)
  const remaining = Math.max(0, totalGuests - ticketsChosen)

  let footer = null
  if (totalGuests <= 0) footer = t("booking_widget.experience.choose_guests_first")
  else if (remaining === 0) footer = t("booking_widget.experience.max_places_reached", { count: totalGuests })

  return (
    <PanelSection
      title={t("booking_widget.experience.add_guest_tickets")}
      subtitle={
        totalGuests > 0 && (
          <span className={`${styles.sectionCaption} ${warning ? styles.sectionCaptionWarning : ""}`}>
            {t("booking_widget.experience.tickets_remaining", { count: remaining })}
          </span>
        )
      }
      error={error}
    >
      <Flex vertical gap={12}>
        {warning && (
          <Flex gap={8} className={styles.availabilityWarning} role="alert">
            <Icon
              type="feather"
              name="AlertTriangle"
              width={20}
              height={20}
              color="currentColor"
              className={styles.availabilityWarningIcon}
            />
            <span>
              <strong>{t("booking_widget.experience.availability_changed")}</strong>: {warning}
            </span>
          </Flex>
        )}
        {ticketTypes.map((ticketType) => {
          const count = Number(ticketCounts?.[ticketType.id] || 0)
          const hasLiveLimit = typeLimits?.[ticketType.id] != null
          const typeLimit = hasLiveLimit ? typeLimits[ticketType.id] : Number(ticketType.max_capacity) || 0
          const isAtTypeLimit = (hasLiveLimit || typeLimit > 0) && count >= typeLimit
          const original = getOriginalIfDiscounted(baseRates[ticketType.id], rates[ticketType.id])
          return (
            <Flex key={ticketType.id} align="center" justify="space-between" gap={12}>
              <Flex vertical>
                <Flex align="center" gap={4}>
                  <span className={styles.guestLabel}>{ticketType.title}</span>
                  {ticketType.description && (
                    <Tooltip title={ticketType.description}>
                      <InfoCircleOutlined
                        className={styles.infoIcon}
                        aria-label={t("booking_widget.experience.ticket_types.info", { title: ticketType.title })}
                      />
                    </Tooltip>
                  )}
                </Flex>
                <span className={styles.guestPrice}>
                  {original != null && <span className={styles.struck}>{numberToCurrency(original)}</span>}
                  {numberToCurrency(rates[ticketType.id])}
                </span>
              </Flex>
              <Counter
                value={count}
                decreaseLabel={t("booking_widget.experience.decrease", { type: ticketType.title })}
                increaseLabel={t("booking_widget.experience.increase", { type: ticketType.title })}
                isDecreaseDisabled={count === 0}
                isIncreaseDisabled={remaining === 0 || isAtTypeLimit}
                onChange={(next) => onChange({ ...ticketCounts, [ticketType.id]: Math.max(0, next) })}
              />
            </Flex>
          )
        })}
        {footer && <span className={styles.sectionCaption}>{footer}</span>}
      </Flex>
    </PanelSection>
  )
}

TicketsSection.propTypes = {
  experience: PropTypes.object.isRequired,
  ticketTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      title: PropTypes.string,
      description: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max_capacity: PropTypes.number
    })
  ).isRequired,
  date: PropTypes.string,
  totalGuests: PropTypes.number.isRequired,
  ticketCounts: PropTypes.object.isRequired,
  typeLimits: PropTypes.objectOf(PropTypes.number),
  warning: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
}

TicketsSection.defaultProps = {
  date: null,
  typeLimits: null,
  warning: null,
  error: null
}

export default TicketsSection
